import {
  askVaFormId,
  CHAPTER_1,
  CHAPTER_2,
  CHAPTER_3,
  USER_ADOPTION, // ask_va_predictive_category
  whoIsYourQuestionAboutLabels,
} from '../constants';
import manifest from '../manifest.json';

import ConfirmationPage from '../containers/ConfirmationPage';
import IntroductionPage from '../containers/IntroductionPage';

// Category and Topic pages

// Your Question
import yourQuestionPage from './chapters/yourQuestion/yourQuestion';

// Your Personal Information - Authenticated
import YourPersonalInformationAuthenticated from '../components/YourPersonalInformationAuthenticated';
import {
  aboutMyselfRelationshipFamilyMemberPages,
  aboutMyselfRelationshipVeteranPages,
  aboutSomeoneElseRelationshipConnectedThroughWorkEducationPages,
  aboutSomeoneElseRelationshipConnectedThroughWorkPages,
  aboutSomeoneElseRelationshipFamilyMemberAboutFamilyMemberPages,
  aboutSomeoneElseRelationshipFamilyMemberAboutVeteranPages,
  aboutSomeoneElseRelationshipFamilyMemberPages,
  aboutSomeoneElseRelationshipVeteranOrFamilyMemberEducationPages,
  aboutSomeoneElseRelationshipVeteranPages,
  generalQuestionPages,
} from './schema-helpers/formFlowHelper';

// // Personal Information
import relationshipToVeteranPage from './chapters/personalInformation/relationshipToVeteran';

// Review Page
import Footer from '../components/Footer';
import CategorySelectPage from '../containers/CategorySelectPage';
// eslint-disable-next-line import/no-cycle
import ReviewPage from '../containers/ReviewPage';
import SubTopicSelectPage from '../containers/SubTopicSelectPage';
import TopicSelectPage from '../containers/TopicSelectPage';
import WhoIsYourQuestionAboutCustomPage from '../containers/WhoIsYourQuestionAboutCustomPage';

// ask_va_predictive_category - Predictive Category Alternative Flow
import PredictiveSuggestedCategorySelectPage from '../containers/PredictiveSuggestedCategorySelectPage';
import simpleQuestionPage from './chapters/simpleQuestion/simpleQuestion';

import CustomPageReviewField from '../components/CustomPageReviewField';
import SignInInterruptPage from '../containers/SignInInterruptPage';
import {
  aboutMyselfRelationshipFamilyMemberCondition,
  aboutMyselfRelationshipVeteranCondition,
  aboutSomeoneElseRelationshipConnectedThroughWorkCondition,
  aboutSomeoneElseRelationshipConnectedThroughWorkEducationCondition,
  aboutSomeoneElseRelationshipFamilyMemberAboutFamilyMemberCondition,
  aboutSomeoneElseRelationshipFamilyMemberAboutVeteranCondition,
  aboutSomeoneElseRelationshipFamilyMemberCondition,
  aboutSomeoneElseRelationshipVeteranCondition,
  aboutSomeoneElseRelationshipVeteranOrFamilyMemberEducationCondition,
  generalQuestionCondition,
  whoIsYourQuestionAboutCondition,
} from './helpers';
import prefillTransformer from './prefill-transformer';
import { askVaMigrations } from './migrations';
import businessPersonalPage from './chapters/businessPersonalPage';

const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  trackingPrefix: 'ask-va-',
  introduction: IntroductionPage,
  confirmation: ConfirmationPage,
  formId: askVaFormId,
  customText: {
    appAction: 'asking your question',
    appSavedSuccessfullyMessage: 'Your question has been saved.',
    appType: 'question',
    continueAppButtonText: 'Continue your question',
    finishAppLaterMessage: 'Finish this question later',
    startNewAppButtonText: 'Start a new question',
  },

  saveInProgress: {
    messages: {
      inProgress: 'Your question is in progress',
      expired: 'Your question expired',
      saved: 'Your question has been saved',
    },
    resumeOnly: false,
  },
  migrations: askVaMigrations,
  version: askVaMigrations.length,
  prefillEnabled: true,
  prefillTransformer,
  savedFormMessages: {
    notFound: 'test notFound',
    noAuth: 'test no auth',
  },
  title: 'Ask VA',
  subTitle: 'Get answers to your questions about VA benefits and services.',
  footerContent: Footer,
  defaultDefinitions: {},
  chapters: {
    categoryAndTopic: {
      title: CHAPTER_1.CHAPTER_TITLE,
      hideFormNavProgress: true,
      pages: {
        yourPersonalInformation: {
          // Auth only - hidden on review page
          path: CHAPTER_3.YOUR_PERSONAL_INFORMATION.PATH,
          title: CHAPTER_3.YOUR_PERSONAL_INFORMATION.TITLE,
          CustomPage: YourPersonalInformationAuthenticated,
          CustomPageReview: null,
          uiSchema: {},
          schema: {
            type: 'object',
            properties: {},
          },
        },
        businessPersonal: {
          path: 'business-or-personal',
          title: 'What type of question do you have?',
          depends: () => localStorage.getItem('hasSession') === 'true',
          editModeOnReviewPage: false,
          uiSchema: businessPersonalPage.uiSchema,
          schema: businessPersonalPage.schema,
        },
        // ask_va_predictive_category - Brief description for AI category suggestion
        simpleQuestionPredictive: {
          path: 'what-do-you-need-help-with',
          title: 'What do you need help with?',
          depends: formData =>
            formData.toggle_ask_va_predictive_category === true &&
            formData.flowType === 'Personal' &&
            localStorage.getItem('hasSession') === 'true',
          editModeOnReviewPage: false,
          uiSchema: simpleQuestionPage.uiSchema,
          schema: simpleQuestionPage.schema,
          updateFormData: simpleQuestionPage.updateFormData,
        },
        // ask_va_predictive_category - AI-suggested categories from prediction API
        suggestedCategoriesPredictive: {
          path: 'select-suggested-category',
          title: 'Suggested categories',
          CustomPage: PredictiveSuggestedCategorySelectPage,
          CustomPageReview: CustomPageReviewField,
          depends: formData =>
            formData.toggle_ask_va_predictive_category === true &&
            formData.flowType === 'Personal' &&
            localStorage.getItem('hasSession') === 'true',
          editModeOnReviewPage: false,
          schema: {
            type: 'object',
            properties: {},
          },
          uiSchema: {},
        },
        selectCategory: {
          path: CHAPTER_1.PAGE_1.PATH,
          title: CHAPTER_1.PAGE_1.TITLE,
          CustomPage: CategorySelectPage,
          CustomPageReview: CustomPageReviewField,
          // ask_va_predictive_category - Non toggle behavior, or user declined/errored on AI suggestions
          depends: formData =>
            formData.toggle_ask_va_predictive_category !== true ||
            formData.predictions?.category?.userAdoption ===
              USER_ADOPTION.DECLINED_SUGGESTIONS ||
            formData.predictions?.category?.userAdoption ===
              USER_ADOPTION.ERROR ||
            formData.flowType === 'Business' ||
            !localStorage.getItem('hasSession'),
          editModeOnReviewPage: false,
          schema: {
            // This does still need to be here or it'll throw an error
            type: 'object',
            properties: {}, // The properties can be empty
          },
          uiSchema: {},
        },
        selectCategorySignInInterrupt: {
          path: 'category-requires-sign-in',
          title: 'Ask VA Sign In',
          CustomPage: SignInInterruptPage,
          CustomPageReview: null,
          editModeOnReviewPage: false,
          depends: formData => formData.categoryRequiresSignIn === true,
          schema: {
            type: 'object',
            properties: {}, // The properties can be empty
          },
          uiSchema: {},
        },
        selectTopic: {
          path: CHAPTER_1.PAGE_2.PATH,
          title: CHAPTER_1.PAGE_2.TITLE,
          CustomPage: TopicSelectPage,
          CustomPageReview: CustomPageReviewField,
          editModeOnReviewPage: false,
          schema: {
            // This does still need to be here or it'll throw an error
            type: 'object',
            properties: {}, // The properties can be empty
          },
          uiSchema: {},
        },
        selectTopicSignInInterrupt: {
          path: 'topic-requires-sign-in',
          title: 'Ask VA Sign In',
          CustomPage: SignInInterruptPage,
          CustomPageReview: null,
          editModeOnReviewPage: false,
          depends: formData => formData.topicRequiresSignIn === true,
          schema: {
            type: 'object',
            properties: {}, // The properties can be empty
          },
          uiSchema: {},
        },
        selectSubtopic: {
          path: CHAPTER_1.PAGE_3.PATH,
          title: CHAPTER_1.PAGE_3.TITLE,
          CustomPage: SubTopicSelectPage,
          CustomPageReview: CustomPageReviewField,
          depends: formData => formData.topicHasSubtopics,
          editModeOnReviewPage: false,
          schema: {
            // This does still need to be here or it'll throw an error
            type: 'object',
            properties: {}, // The properties can be empty
          },
          uiSchema: {},
        },
      },
    },
    yourQuestionPart1: {
      title: 'Your Question Part 1',
      hideFormNavProgress: true,
      pages: {
        whoIsYourQuestionAbout: {
          editModeOnReviewPage: false,
          path: CHAPTER_2.PAGE_1.PATH,
          title: CHAPTER_2.PAGE_1.TITLE,
          CustomPage: WhoIsYourQuestionAboutCustomPage,
          CustomPageReview: CustomPageReviewField,
          depends: formData => {
            return whoIsYourQuestionAboutCondition(formData);
          },
          schema: {
            // This does still need to be here or it'll throw an error
            type: 'object',
            properties: {}, // The properties can be empty
          },
          uiSchema: {},
        },
        yourQuestionSignInInterrupt: {
          path: 'your-question-requires-sign-in',
          title: 'Ask VA Sign In',
          CustomPage: SignInInterruptPage,
          CustomPageReview: null,
          editModeOnReviewPage: false,
          depends: formData => formData.yourQuestionRequiresSignIn === true,
          schema: {
            type: 'object',
            properties: {}, // The properties can be empty
          },
          uiSchema: {},
        },
        relationshipToVeteran: {
          editModeOnReviewPage: false,
          path: CHAPTER_3.RELATIONSHIP_TO_VET.PATH,
          title: CHAPTER_3.RELATIONSHIP_TO_VET.TITLE,
          CustomPageReview: CustomPageReviewField,
          depends: formData => {
            return (
              formData.whoIsYourQuestionAbout !==
              whoIsYourQuestionAboutLabels.GENERAL
            );
          },
          uiSchema: relationshipToVeteranPage.uiSchema,
          schema: relationshipToVeteranPage.schema,
        },
      },
    },
    aboutMyselfRelationshipVeteran: {
      title: CHAPTER_3.CHAPTER_TITLE,
      hideFormNavProgress: true,
      depends: aboutMyselfRelationshipVeteranCondition,
      pages: { ...aboutMyselfRelationshipVeteranPages },
    },
    aboutMyselfRelationshipFamilyMember: {
      title: CHAPTER_3.CHAPTER_TITLE,
      hideFormNavProgress: true,
      depends: aboutMyselfRelationshipFamilyMemberCondition,
      pages: { ...aboutMyselfRelationshipFamilyMemberPages },
    },
    aboutSomeoneElseRelationshipVeteran: {
      title: CHAPTER_3.CHAPTER_TITLE,
      hideFormNavProgress: true,
      depends: aboutSomeoneElseRelationshipVeteranCondition,
      pages: { ...aboutSomeoneElseRelationshipVeteranPages },
    },
    aboutSomeoneElseRelationshipFamilyMember: {
      title: CHAPTER_3.CHAPTER_TITLE,
      hideFormNavProgress: true,
      depends: aboutSomeoneElseRelationshipFamilyMemberCondition,
      pages: { ...aboutSomeoneElseRelationshipFamilyMemberPages },
    },
    aboutSomeoneElseRelationshipFamilyMemberAboutVeteran: {
      title: CHAPTER_3.CHAPTER_TITLE,
      hideFormNavProgress: true,
      depends: aboutSomeoneElseRelationshipFamilyMemberAboutVeteranCondition,
      pages: {
        ...aboutSomeoneElseRelationshipFamilyMemberAboutVeteranPages,
      },
    },
    aboutSomeoneElseRelationshipFamilyMemberAboutFamilyMember: {
      title: CHAPTER_3.CHAPTER_TITLE,
      hideFormNavProgress: true,
      depends:
        aboutSomeoneElseRelationshipFamilyMemberAboutFamilyMemberCondition,
      pages: {
        ...aboutSomeoneElseRelationshipFamilyMemberAboutFamilyMemberPages,
      },
    },
    aboutSomeoneElseRelationshipConnectedThroughWork: {
      title: CHAPTER_3.CHAPTER_TITLE,
      hideFormNavProgress: true,
      depends: aboutSomeoneElseRelationshipConnectedThroughWorkCondition,
      pages: { ...aboutSomeoneElseRelationshipConnectedThroughWorkPages },
    },
    aboutSomeoneElseRelationshipConnectedThroughWorkEducation: {
      title: CHAPTER_3.CHAPTER_TITLE,
      hideFormNavProgress: true,
      depends:
        aboutSomeoneElseRelationshipConnectedThroughWorkEducationCondition,
      pages: {
        ...aboutSomeoneElseRelationshipConnectedThroughWorkEducationPages,
      },
    },
    aboutSomeoneElseRelationshipVeteranOrFamilyMemberEducation: {
      title: CHAPTER_3.CHAPTER_TITLE,
      hideFormNavProgress: true,
      depends:
        aboutSomeoneElseRelationshipVeteranOrFamilyMemberEducationCondition,
      pages: {
        ...aboutSomeoneElseRelationshipVeteranOrFamilyMemberEducationPages,
      },
    },
    generalQuestion: {
      title: CHAPTER_3.CHAPTER_TITLE,
      hideFormNavProgress: true,
      depends: generalQuestionCondition,
      pages: { ...generalQuestionPages },
    },
    yourQuestionPart2: {
      title: CHAPTER_2.CHAPTER_TITLE,
      hideFormNavProgress: true,
      pages: {
        question: {
          path: CHAPTER_2.PAGE_3.PATH,
          title: yourQuestionPage.title,
          // CustomPageReview: CustomYourQuestionReviewField,
          uiSchema: yourQuestionPage.uiSchema,
          schema: yourQuestionPage.schema,
          onNavForward: ({ goPath }) => {
            goPath('/review-then-submit');
          },
        },
      },
    },

    review: {
      title: 'Review and submit',
      hideFormNavProgress: true,
      pages: {
        reviewForm: {
          title: 'Review and submit',
          path: 'review-then-submit',
          CustomPage: ReviewPage,
          CustomPageReview: null,
          uiSchema: {},
          schema: {
            type: 'object',
            properties: {},
          },
          onNavForward: ({ goPath }) => {
            goPath('/confirmation');
          },
        },
      },
    },
  },
};

export default formConfig;
